import { useParams, type RouteObject } from 'react-router';
import type { ReactElement } from 'react';
import { READABLE_OFFLINE } from '../../core/pwa/works-offline';

/**
 * Two routes. `/tags` is the cloud (011 FR-025) and `/tags/:name` is one tag's view (FR-016), and
 * the index route must come first: a router that met `:name` first would match the cloud's own URL
 * with an empty name.
 *
 * The tag view's segment is the tag's **name** — no id and no slug (research R-002: a tag name
 * must never occupy an *API* path segment, but the SPA's own URL is where the name belongs, since it
 * is all a chip on a row has to link with).
 *
 * `:name` reaches `TagPage` as a prop, read from `useParams()` inside the route's own element — the
 * shape `containers.routes.tsx` uses and for the reason given there. Angular needed
 * `withComponentInputBinding()` in `app.config.ts` for the equivalent, a one-line configuration that
 * could be dropped with no type error; here there is nothing to drop. The router decodes the segment
 * before it reaches `useParams`, so a name with spaces, a slash, a `?`, a `#` or non-ASCII arrives as
 * the practitioner typed it and goes to the API unchanged.
 */
export const routes: RouteObject[] = [
  {
    index: true,
    // 036 (issue 221, FR-021): the tag cloud is readable offline.
    handle: { [READABLE_OFFLINE]: true },
    lazy: async () => ({ Component: (await import('./tags-page')).default }),
  },
  {
    path: ':name',
    // 036 (issue 221, FR-021): a single tag's own page is readable offline.
    handle: { [READABLE_OFFLINE]: true },
    lazy: async () => {
      const { default: TagPage } = await import('./tag-page');
      function TagRoute(): ReactElement {
        const { name } = useParams();
        return <TagPage name={name ?? ''} />;
      }
      return { Component: TagRoute };
    },
  },
];
